<!-- eslint-disable vue/no-mutating-props -->
<!-- Linting error fixes broke the model update logic completely. Decided to disable it -->
<template>
  <div class="flex-1">
    <div v-if="model" class="grid grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-3 gap-4">
      <FormLazySelect
        v-if="allowClassificationPrefilled"
        :key="`${namePrefix}pointOfSaleId`"
        v-model="model.pointOfSaleId"
        :data-testid="`${dataTestidPrefix}${namePrefix}-pos-select`"
        :name="`${computedFormPrefix}pointOfSaleId`"
        label="pos"
        :fetchPagedData="fetchPointOfSalesData"
        :fetchSingleItem="fetchSinglePointOfSale"
        :initialValue="initialValues.pointOfSaleId"
        :disabled="!editMode"
        showClear
        filter
        :isDirty="() => false"
      />
      <FormSelect
        :key="`${namePrefix}documentType`"
        v-model="model.documentType"
        :data-testid="`${dataTestidPrefix}${namePrefix}-doctype-select`"
        :name="`${computedFormPrefix}documentType`"
        label="documentType"
        :options="Object.values(CreateTicketFromEMailV1Dto.documentType)"
        localM
        :initialValue="initialValues.documentType"
        :globalDefault="props.globalDefault?.documentType"
        :disabled="!editMode"
        showClear
        filter
        :isDirty="() => true"
      />
      <FormLazySelect
        v-if="allowClassificationPrefilled"
        :key="`${namePrefix}ticketId`"
        v-model="model.ticketId"
        :data-testid="`${dataTestidPrefix}${namePrefix}-ticket-select`"
        :name="`${computedFormPrefix}ticketId`"
        label="ticket"
        :fetchPagedData="fetchTicketsData"
        :fetchSingleItem="fetchSingleTicket"
        :initialValue="initialValues.ticketId"
        :disabled="!editMode"
        showClear
        filter
        :isDirty="() => false"
        @userChange="
          (e: SelectChangeEvent) => {
            emit('update-user:ticketId', e);
          }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue';

import type {
  LazySelectFetchOptions,
  PaginatedResponse,
} from '@/components/forms/hooks/useLazySelect';
import {
  CreateTicketFromEMailV1Dto,
  type PointOfSaleSimpleV2Dto,
  TicketDocumentV1Dto,
  type TicketSimpleV2Dto,
} from '@/openapi';

interface TicketSelectionData {
  pointOfSaleId?: string;
  documentType?: TicketDocumentV1Dto.documentType;
  ticketId?: string;
}

const props = withDefaults(
  defineProps<{
    model: TicketSelectionData;
    namePrefix: string;
    formPrefix?: string;
    fetchPointOfSalesData: (
      options: LazySelectFetchOptions,
    ) => Promise<PaginatedResponse<PointOfSaleSimpleV2Dto> | undefined>;
    fetchTicketsData: (
      options: LazySelectFetchOptions,
    ) => Promise<PaginatedResponse<TicketSimpleV2Dto> | undefined>;
    fetchSinglePointOfSale: (id: string) => Promise<PointOfSaleSimpleV2Dto | null | undefined>;
    fetchSingleTicket: (id: string) => Promise<TicketSimpleV2Dto | null | undefined>;
    allowClassificationPrefilled?: boolean;
    initialData: TicketSelectionData;
    globalDefault?: TicketSelectionData;
    editMode?: boolean;
    dataTestidPrefix?: string;
  }>(),
  {
    formPrefix: '',
    allowClassificationPrefilled: true,
    globalDefault: () => ({}),
    editMode: true,
    dataTestidPrefix: '',
  },
);

const initialValues = ref({
  pointOfSaleId: props.model.pointOfSaleId,
  documentType: props.model.documentType,
  ticketId: props.model.ticketId,
});

const emit = defineEmits([
  'update:pointOfSaleId',
  'update:documentType',
  'update:ticketId',
  'update-user:ticketId',
  'removeTempTicket',
  'dirty',
]);

const isDirty = computed(() => {
  return (
    props.model.pointOfSaleId !== props.initialData.pointOfSaleId ||
    props.model.documentType !== props.initialData.documentType ||
    props.model.ticketId !== props.initialData.ticketId
  );
});

const computedFormPrefix = computed(() => {
  return props.formPrefix ? `${props.formPrefix}.` : '';
});

watch(
  () => props.model.pointOfSaleId,
  (newValue) => {
    emit('update:pointOfSaleId', newValue);
  },
);

watch(
  () => props.model.documentType,
  (newValue) => {
    emit('update:documentType', newValue);
  },
);

watch(
  () => props.model.ticketId,
  (newValue, oldValue) => {
    emit('update:ticketId', newValue);

    // Clear pointOfSaleId when ticketId changes to prevent invalid state
    // Only clear if ticket actually changed (not on initial mount)
    if (oldValue !== undefined && newValue !== oldValue) {
      emit('update:pointOfSaleId', undefined);
    }
  },
);

watch(isDirty, (newValue) => {
  emit('dirty', newValue);
});

watch(
  () => props.initialData,
  (newData) => {
    initialValues.value = {
      pointOfSaleId: newData.pointOfSaleId,
      documentType: newData.documentType,
      ticketId: newData.ticketId,
    };
  },
  { immediate: true, deep: true },
);
</script>
